import { topicColorResolved } from "@app/core";
import { cn, CheckCircle2, Trash2 } from "@app/ui";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion, springs, useReducedMotion } from "../../../lib/motion/index.js";
import { useI18n } from "../../../lib/useI18n.js";
import {
  useBlockMoveGhostStore,
  useMobileSheetDraftStore,
  useSettingsStore,
  useSheetMorphStore,
  useTaskDialogStore,
  useTasksStore,
  useTopicsStore,
  useTouchMoveStore,
} from "../../../stores/index.js";
import { useTaskContextColor } from "../../useTaskContextColor.js";
import { useThemeStore } from "../../../stores/theme.store.js";
import { contrastShade, matrixAccentFromPos, mixColors, pastel, PRIORITY_FLAG_HEX } from "../../task-colors.js";
import { PriorityMatrix } from "../../priority-matrix/PriorityMatrix.js";
import { matrixDisplayWithUrgency, useDeadlineUrgencyValue } from "../../priority-matrix/useEffectiveMatrixDisplay.js";
import { formDeadlineIso } from "../../task-form/time-helpers.js";
import { useTaskForm } from "../../task-form/useTaskForm.js";
import { suppressCalendarBackgroundCreate } from "../../calendar-view/calendar-background-create-guard.js";
import { TitleField } from "./fields/TitleField.js";
import { PriorityField } from "./fields/PriorityField.js";
import { TopicButtons } from "./fields/TopicButtons.js";
import { ScheduleField } from "./fields/ScheduleField.js";
import { LocationFieldMobile } from "./fields/LocationFieldMobile.js";
import { NotesField } from "./fields/NotesField.js";
import { RepeatReminderFields } from "./fields/RepeatReminderFields.js";
import { AdvancedFields } from "./fields/AdvancedFields.js";
import { ColorField } from "./fields/ColorField.js";
import { SheetWash, TOP_SOFT, type WashPick } from "./SheetWash.js";
import { useSheetDrag } from "./useSheetDrag.js";
import { useGhostSync } from "./useGhostSync.js";
import "./styles/sheet.css";

function blurActiveField(): void {
  (document.activeElement as HTMLElement | null)?.blur?.();
}

function swallowTapThrough(ms = 650): void {
  const swallow = (ev: Event) => {
    ev.preventDefault();
    ev.stopPropagation();
    ev.stopImmediatePropagation?.();
  };
  const opts = { capture: true } as const;
  window.addEventListener("pointerup", swallow, opts);
  window.addEventListener("touchend", swallow, opts);
  window.addEventListener("click", swallow, opts);
  window.setTimeout(() => {
    window.removeEventListener("pointerup", swallow, opts);
    window.removeEventListener("touchend", swallow, opts);
    window.removeEventListener("click", swallow, opts);
  }, ms);
}

/**
 * Mobile create/edit bottom sheet for every create/edit entry point.
 */
export function MobileTaskSheet() {
  const { t } = useI18n();
  const form = useTaskForm();
  const formRef = useRef(form);
  formRef.current = form;
  const { open, editing } = form;
  const reduce = useReducedMotion();
  const mobileTopicDisplay = useSettingsStore((s) => s.mobileTopicDisplay);
  const mobileTopicMulti = useSettingsStore((s) => s.mobileTopicMulti);
  const setMobileSheetHeight = useSettingsStore((s) => s.setMobileSheetHeight);
  const topicColors = useTopicColors(form.topicIds);
  // When an on-grid draft ghost is active (create-from-calendar), the scrim must
  // let touches through so the ghost stays draggable/resizable and a tap on it
  // doesn't dismiss the sheet (the dialog store ignores re-triggers while open).
  const ghostActive = useMobileSheetDraftStore((s) => s.active);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLDivElement | null>(null);
  const topAreaRef = useRef<HTMLDivElement | null>(null);
  const titleAreaRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const titleWasEditing = useRef(false);
  const wasOpenRef = useRef(false);
  const [openSeq, setOpenSeq] = useState(0);
  const [pick, setPick] = useState<WashPick | null>(null);
  const pickSeq = useRef(0);

  const { h, mode, onHandlePointerDown, onGripPointerDown, collapse, ensureHeight } = useSheetDrag({
    open,
    reduce: !!reduce,
    editing: !!editing,
    onCommitPartial: (f) => void setMobileSheetHeight(f),
    onClose: form.requestClose,
  });
  useGhostSync(form);

  const contextColor = useTaskContextColor({ calendar_id: form.calendarId, topic_ids: form.topicIds });
  const accent = form.customColor ?? contextColor;
  // Whenever the matrix system covers this entry it drives priority directly
  // (the on-sheet matrix). Flags (PriorityField) show only when off-scope.
  const matrixMode = form.matrixInScope;
  // Urgency auto-derived from the entry's deadline (deadline_at ?? due_at) for an
  // unplaced entry — shifts the ghost dot + border toward red as the deadline nears.
  const deadlineIso = formDeadlineIso(form);
  const deadlineUrgency = useDeadlineUrgencyValue(deadlineIso, form.tz);
  // Matrix mode: the sheet border follows the matrix's diagonal green→red
  // gradient at the entry's (possibly deadline-derived) position.
  const priorityColor = matrixMode
    ? matrixAccentFromPos(matrixDisplayWithUrgency(form.matrixPos, form.priority, deadlineUrgency))
    : PRIORITY_FLAG_HEX[form.priority] || null;
  // Readable ink over the topic wash band (mirrors SheetWash's pastel softening)
  // so title/location text adapts when a topic colors the background.
  const topWashColor = topicColors.length > 0 ? pastel(mixColors(topicColors)!, TOP_SOFT) : null;
  const washInk = topWashColor ? contrastShade(topWashColor) : null;

  useLayoutEffect(() => {
    if (open && !wasOpenRef.current) setOpenSeq((n) => n + 1);
    wasOpenRef.current = open;
  }, [open]);

  // Escape (also Android back, dispatched on the dialog): defer to an open
  // picker, then collapse full→partial, then request close. Bail while a
  // higher confirm dialog is up.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (document.querySelector(".sheet-modal")) return;
      if (document.querySelectorAll('[role="dialog"]').length > 1) return;
      // Through the ref, never the captured `form`: this listener binds once per
      // open (deps [open, mode], and `mode` doesn't change on its own), so a
      // captured form would keep the state from the render BEFORE useTaskForm's
      // load effect ran — an untouched task then always reads as dirty against
      // the (ref-held, already loaded) baseline and Escape only ever re-opened
      // the discard confirm.
      const latest = formRef.current;
      if (latest.discardConfirmOpen) { latest.cancelDiscard(); return; }
      if (mode === "full") { collapse(); return; }
      blurActiveField();
      latest.requestClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  useEffect(() => {
    if (!open) return;
    const onOutsidePointer = (e: PointerEvent) => {
      const latest = formRef.current;
      if (latest.discardConfirmOpen) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest(".task-sheet-root, .sheet-modal, [data-quick-ghost]")) return;
      suppressCalendarBackgroundCreate();
      swallowTapThrough();
      useTouchMoveStore.getState().setMoving(null);
      useBlockMoveGhostStore.getState().end();
      e.stopPropagation();
      e.stopImmediatePropagation?.();
      blurActiveField();
      if (e.cancelable) e.preventDefault();
      latest.requestClose();
    };
    window.addEventListener("pointerdown", onOutsidePointer, true);
    return () => window.removeEventListener("pointerdown", onOutsidePointer, true);
  }, [open]);

  const firePick = (color: string, rect: DOMRect, kind: WashPick["kind"]) => {
    pickSeq.current += 1;
    setPick({ id: pickSeq.current, color, originRect: rect, kind });
  };

  // Hold the shared body scroller pinned to the top across a short window. A
  // single synchronous scrollTop=0 isn't enough: committing the title dismisses
  // the keyboard, and Android's WebView then fires async visualViewport reflows
  // that re-scroll the focused-input offset back in — landing the view below the
  // bottom fields. Re-pinning each frame for ~500ms outlasts that reflow.
  const pinScrollRaf = useRef(0);
  const pinScrollTop = () => {
    cancelAnimationFrame(pinScrollRaf.current);
    const start = performance.now();
    const step = () => {
      const body = bodyRef.current;
      if (!body) return;
      body.scrollTop = 0;
      if (performance.now() - start < 500) pinScrollRaf.current = requestAnimationFrame(step);
    };
    step();
  };
  useEffect(() => () => cancelAnimationFrame(pinScrollRaf.current), []);

  const revealTopArea = () => {
    pinScrollTop();
    requestAnimationFrame(() => {
      const panel = panelRef.current?.getBoundingClientRect();
      const topArea = topAreaRef.current?.getBoundingClientRect();
      if (panel && topArea) ensureHeight(Math.ceil(topArea.bottom - panel.top + 8));
      else ensureHeight(112);
    });
  };

  const onTitleEditingChange = (isEditing: boolean) => {
    const wasEditing = titleWasEditing.current;
    titleWasEditing.current = isEditing;
    if (wasEditing && !isEditing) revealTopArea();
  };

  useEffect(() => {
    if (!open || editing || !form.title.trim()) return;
    // While the title input is focused, growing the sheet under the keyboard
    // makes the whole window creep upward on every keystroke — the reveal
    // happens on commit instead (onCommit → revealTopArea).
    if (titleWasEditing.current) return;
    revealTopArea();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, form.title]);

  const location = useLocation();
  // Save, then (on a calendar route) "drop" the sheet into the new block's slot.
  const onSave = async () => {
    const onCal = location.pathname.startsWith("/calendar");
    const rect = panelRef.current?.getBoundingClientRect();
    const preIds = editing ? null : new Set(useTasksStore.getState().tasks.map((t) => t.id));
    await form.submit();
    if (!onCal || !rect) return;
    if (useTaskDialogStore.getState().open) return; // cancelled / validation error
    let taskId: string | null = null;
    if (editing) {
      taskId = editing.virtual ? null : editing.id; // recurring-occurrence edits skip the morph
    } else {
      taskId = useTasksStore.getState().tasks
        .find((t) => !preIds!.has(t.id) && !t.subtask_parent_id && !t.deleted_at && !t.virtual)?.id ?? null;
    }
    if (taskId) {
      useSheetMorphStore.getState().fire(taskId, { left: rect.left, top: rect.top, width: rect.width, height: rect.height });
    }
  };

  const showReminders = !!form.dateStr;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className={cn("fixed inset-0 z-[45]", ghostActive ? "pointer-events-none bg-black/20" : "bg-black/40")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, pointerEvents: "none" }}
            transition={reduce ? { duration: 0 } : { duration: 0.2 }}
          />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={editing ? t("taskForm.editTask") : t("taskForm.newTask")}
            className="task-sheet-root fixed inset-x-0 bottom-0 z-[45] flex flex-col overflow-hidden rounded-t-2xl border-2 border-border bg-card shadow-2xl transition-[border-color] duration-500"
            style={{ height: h, borderColor: priorityColor ?? undefined, paddingTop: mode === "full" ? "env(safe-area-inset-top)" : undefined }}
            initial={reduce ? { opacity: 0 } : { y: "100%" }}
            animate={reduce ? { opacity: 1 } : { y: 0 }}
            exit={reduce ? { opacity: 0, pointerEvents: "none" } : { y: "100%", pointerEvents: "none" }}
            transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 38 }}
          >
            {/* In matrix mode the topic band ends above the matrix row (the
                matrix sits on the plain body background); in flag mode it
                covers the whole top area as before. */}
            <SheetWash
              panelRef={panelRef}
              titleRef={titleRef}
              boundaryRef={matrixMode ? titleAreaRef : topAreaRef}
              topicColors={topicColors}
              bodyColor={form.customColor}
              priorityColor={priorityColor}
              pick={pick}
            />

            {/* Drag handle (resize / fling) — short + wide */}
            <div className="relative z-10 flex shrink-0 cursor-grab touch-none justify-center py-[6px]" onPointerDown={onHandlePointerDown}>
              <span className="h-[1.7px] w-[110px] rounded-full bg-muted-foreground/40" />
            </div>

            {/* Scrollable form content. Title/priority live in the same scroller
                as the rest of the fields so Android's input auto-scroll cannot
                split the sheet into a fixed header plus an offset body. */}
            <div ref={bodyRef} className="no-scrollbar relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto">
            {/* Top color area: Title/Location + unified create/type control, then
                either matrix+topics or the compact flag priority selector. */}
            <div
              ref={topAreaRef}
              className={cn("shrink-0 touch-none", !matrixMode && washInk && "sheet-ink")}
              style={!matrixMode && washInk ? { color: washInk } : undefined}
              onPointerDown={onGripPointerDown}
            >
              <div
                ref={titleAreaRef}
                className={cn("relative z-10 grid shrink-0 grid-cols-[minmax(0,1fr)_7.5rem] gap-x-2 px-3 pt-1", matrixMode && washInk && "sheet-ink")}
                style={matrixMode && washInk ? { color: washInk } : undefined}
              >
                <div ref={titleRef} className="col-start-1 row-start-1 min-w-0">
                  <TitleField
                    value={form.title}
                    onChange={form.setTitle}
                    autoFocus={!editing}
                    focusKey={openSeq}
                    accent={washInk ?? accent}
                    onCommit={revealTopArea}
                    onEditingChange={onTitleEditingChange}
                  />
                </div>

                <div className="col-start-1 row-start-2 min-w-0">
                  <LocationFieldMobile value={form.location} onChange={form.setLocation} />
                </div>

                <div className="col-start-2 row-span-2 row-start-1 flex self-center overflow-hidden rounded-xl bg-muted/70 p-0.5 shadow-sm">
                  <div className="flex w-full flex-col gap-0.5">
                    <button
                      type="button"
                      className="h-8 w-full rounded-t-md rounded-b-sm bg-primary/60 px-3 text-sm font-semibold text-primary-foreground transition-colors active:bg-primary/75 disabled:opacity-50"
                      disabled={form.submitting}
                      onClick={() => void onSave()}
                    >
                      {editing ? t("common.save") : t("common.create")}
                    </button>
                    <EventTaskSlider isEvent={form.isEvent} onChange={form.setIsEvent} reduce={!!reduce} />
                  </div>
                </div>
              </div>

              {matrixMode ? (
                <div className="relative z-10 flex items-stretch gap-2 px-3 pb-2 pt-2">
                  <div className="shrink-0 touch-none" onPointerDown={(e) => e.stopPropagation()}>
                    <PriorityMatrix
                      value={form.matrixPos}
                      priority={form.priority}
                      onChange={form.setMatrixPos}
                      deadlineUrgency={deadlineUrgency}
                      hidden={form.matrixHidden}
                      onHiddenChange={form.setMatrixHidden}
                    />
                  </div>
                  <div className="h-[13rem] min-w-0 flex-1" onPointerDown={(e) => e.stopPropagation()}>
                    <TopicButtons
                      topicIds={form.topicIds}
                      setTopicIds={form.setTopicIds}
                      display={mobileTopicDisplay}
                      multi={mobileTopicMulti}
                      orientation="vertical"
                      className="h-full px-0"
                      onPick={({ color, rect }) => firePick(color, rect, "topic")}
                    />
                  </div>
                </div>
              ) : (
                <div className="relative z-10 flex justify-center px-3 pb-2 pt-2">
                  <PriorityField value={form.priority} onChange={form.setPriority} onPick={({ color, rect }) => firePick(color, rect, "priority")} />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 px-3 py-2" style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}>
              {!matrixMode && (
                <TopicButtons
                  topicIds={form.topicIds}
                  setTopicIds={form.setTopicIds}
                  display={mobileTopicDisplay}
                  multi={mobileTopicMulti}
                  onPick={({ color, rect }) => firePick(color, rect, "topic")}
                />
              )}

              <ColorField
                value={form.customColor}
                onChange={form.setCustomColor}
                onPick={({ color, rect }) => firePick(color, rect, "color")}
              />

              <ScheduleField
                dateStr={form.dateStr}
                setDateStr={form.setDateStr}
                hour={form.hour}
                setHour={form.setHour}
                minute={form.minute}
                setMinute={form.setMinute}
                allDay={form.allDay}
                setAllDay={form.setAllDay}
                duration={form.duration}
                setDuration={form.setDuration}
                isEvent={form.isEvent}
                deadlineStr={form.deadlineStr}
                setDeadlineStr={form.setDeadlineStr}
              />

              <NotesField value={form.notes} onChange={form.setNotes} />

              <RepeatReminderFields
                recurEnabled={form.recurEnabled}
                setRecurEnabled={form.setRecurEnabled}
                recurRule={form.recurRule}
                setRecurRule={form.setRecurRule}
                seriesUntilStr={form.seriesUntilStr}
                setSeriesUntilStr={form.setSeriesUntilStr}
                reminderOffsets={form.reminderOffsets}
                setReminderOffsets={form.setReminderOffsets}
                showReminders={showReminders}
                editing={editing}
                pendingSubtasks={form.pendingSubtasks}
                setPendingSubtasks={form.setPendingSubtasks}
              />

              <AdvancedFields
                calendarId={form.calendarId}
                setCalendarId={form.setCalendarId}
                labelIds={form.labelIds}
                setLabelIds={form.setLabelIds}
                tz={form.tz}
                setTz={form.setTz}
              />

              {form.error && <p className="px-1 text-sm text-destructive">{form.error}</p>}

              {editing && (
                <div className="mt-1 flex items-center gap-2 border-t pt-3">
                  <button type="button" className="flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm" onClick={() => void form.handleComplete()}>
                    <CheckCircle2 className="h-4 w-4" />
                    {editing.status === "done" ? t("taskForm.reopen") : t("common.complete")}
                  </button>
                  <button type="button" className="ml-auto flex items-center gap-1.5 rounded-md border border-destructive/40 px-3 py-2 text-sm text-destructive" onClick={() => void form.deleteIt()}>
                    <Trash2 className="h-4 w-4" /> {t("common.delete")}
                  </button>
                </div>
              )}
            </div>
            </div>
          </motion.div>

          {form.discardConfirmOpen && (
            <div
              className="fixed inset-0 z-[49] grid place-items-center bg-black/50 p-4"
              onPointerDown={(e) => { if (e.target === e.currentTarget) form.cancelDiscard(); }}
            >
              <div className="w-full max-w-xs rounded-xl border bg-card p-4 shadow-2xl" onPointerDown={(e) => e.stopPropagation()}>
                <h2 className="mb-1 font-medium">{editing ? t("taskForm.discardChangesTitle") : t("taskForm.discardTitle")}</h2>
                <p className="mb-3 text-sm text-muted-foreground">{t("taskForm.discardMobileCopy", { kind: editing ? t("taskForm.changes") : t("taskForm.details") })}</p>
                <div className="flex justify-end gap-2">
                  <button type="button" className="rounded-md border px-3 py-2 text-sm" onClick={form.cancelDiscard}>{t("taskForm.keepEditing")}</button>
                  <button type="button" className="rounded-md bg-destructive px-3 py-2 text-sm text-destructive-foreground" onClick={form.confirmDiscard}>{t("taskForm.discard")}</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </AnimatePresence>
  );
}

/** Event ⇄ Task as a low-contrast sliding toggle (a thumb glides under the
 *  active label) — visually reads as a slider, not two buttons. */
function EventTaskSlider({ isEvent, onChange, reduce }: { isEvent: boolean; onChange: (e: boolean) => void; reduce: boolean }) {
  const { t } = useI18n();
  return (
    <div className="relative flex h-8 w-full rounded-t-sm rounded-b-md bg-background/35 p-0.5 text-xs font-medium">
      <motion.span
        aria-hidden
        className="absolute inset-y-0.5 left-0.5 w-[calc(50%-2px)] rounded-md bg-background shadow-sm"
        animate={{ x: isEvent ? 0 : "100%" }}
        transition={reduce ? { duration: 0 } : springs.snappy}
      />
      <button type="button" className={cn("relative z-10 flex flex-1 items-center justify-center rounded-md transition-colors", isEvent ? "text-foreground" : "text-muted-foreground")} onClick={() => onChange(true)}>{t("taskForm.event")}</button>
      <button type="button" className={cn("relative z-10 flex flex-1 items-center justify-center rounded-md transition-colors", !isEvent ? "text-foreground" : "text-muted-foreground")} onClick={() => onChange(false)}>{t("taskForm.task")}</button>
    </div>
  );
}

/** The colors of the currently-selected topics, in order — resolved from the
 *  THEME (override or categorical palette by global index), exactly like the
 *  topic buttons / calendar blocks, so the wash matches the configured colors. */
function useTopicColors(topicIds: string[]): string[] {
  const topics = useTopicsStore((s) => s.topics);
  const theme = useThemeStore((s) => s.config);
  return useMemo(() => {
    return topicIds
      .map((id) => {
        const i = topics.findIndex((t) => t.id === id);
        return i >= 0 ? topicColorResolved(theme, id, i) : null;
      })
      .filter((c): c is string => !!c);
  }, [topicIds, topics, theme]);
}
